import { TrendingDown, TrendingFlat, TrendingUp } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { yellow } from '@mui/material/colors';
import {
  DEFAULT_NUMBER_OPTIONS,
  formatNumber,
  formatPercentageValue,
  intlFormatNumber,
} from 'src/utils/number';

type IndicatorCellItemProps = {
  value: number | string | null | 'N/D';
  type?: 'amount' | 'percentage';
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  status?: 'positive' | 'negative' | 'neutral' | undefined;
  criticality?: 'none' | 'healthy' | 'alert' | 'critical';
  informative?: boolean;
};

export function IndicatorCellItem({
  value,
  type,
  onClick,
  status,
  criticality,
  informative = false,
}: IndicatorCellItemProps) {
  function getTrendingIcon() {
    switch (status) {
      case 'positive':
        return <TrendingUp sx={{ mr: 1 }} fontSize="small" />;
      case 'negative':
        return <TrendingDown sx={{ mr: 1 }} fontSize="small" />;
      case 'neutral':
        return <TrendingFlat sx={{ mr: 1 }} fontSize="small" />;
      default:
        return <TrendingFlat sx={{ mr: 1 }} fontSize="small" />;
    }
  }

  function getAlertColor() {
    if (informative) {
      return {
        borderColor: 'grey.300',
        bgcolor: 'grey.100',
        color: 'grey.500',
      };
    }

    if (criticality === 'critical') {
      return {
        borderColor: 'error.main',
        bgcolor: 'error.light',
        color: 'error.main',
      };
    }

    if (criticality === 'alert') {
      return {
        borderColor: yellow[600],
        bgcolor: yellow[100],
        color: yellow[800],
      };
    }

    if (criticality === 'healthy') {
      return {
        borderColor: 'success.main',
        bgcolor: 'success.light',
        color: 'success.main',
      };
    }

    return {
      borderColor: 'grey.300',
      bgcolor: 'grey.100',
      color: 'grey.500',
    };
  }

  const label =
    value === 'N/D'
      ? 'N/D'
      : value === null || value === 'N/A'
        ? 'N/A'
        : type === 'amount'
          ? formatNumber(
              value,
              DEFAULT_NUMBER_OPTIONS.locale,
              DEFAULT_NUMBER_OPTIONS.minimumFractionDigits,
              DEFAULT_NUMBER_OPTIONS.maximumFractionDigits,
            )
          : formatPercentageValue(
              value,
              DEFAULT_NUMBER_OPTIONS.maximumFractionDigits,
            );

  const formatter = (value: number | string | null | 'N/D') => {
    if (value === 'N/D') {
      return 'N/D';
    }

    if (value === null || value === 'N/A') {
      return 'N/A';
    }

    if (type === 'amount') {
      return intlFormatNumber(
        value,
        DEFAULT_NUMBER_OPTIONS.maximumFractionDigits,
      );
    }

    return formatPercentageValue(
      value,
      DEFAULT_NUMBER_OPTIONS.maximumFractionDigits,
    );
  };

  return (
    <Tooltip arrow title={formatter(value) || ''}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius={2}
        py={0.5}
        px={1}
        border="1px solid"
        borderColor={getAlertColor().borderColor}
        bgcolor={getAlertColor().bgcolor}
        color={getAlertColor().color}
        sx={{
          '&:hover': {
            cursor: informative ? 'default' : 'pointer',
          },
          width: 'auto',
          minHeight: '32px',
        }}
        onClick={informative ? undefined : onClick}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ width: 'auto' }}>
          {!informative &&
            label !== 'N/A' &&
            label !== 'N/D' &&
            getTrendingIcon()}
          <Typography
            fontSize={14}
            sx={{
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}>
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}
